import { useEffect, useRef, useState } from 'react'
import * as S from './styles'

export interface DropdownOption {
  key: string
  label: string
  disabled?: boolean
}

interface FilterDropdownProps {
  id?: string
  currentKey: string
  options: DropdownOption[]
  onChange: (key: string) => void
  fixedLabel?: string
  labelPrefix?: string
  /** 'popover' — absolute dropdown with click-outside close (desktop).
   *  'inline'  — expanding list with RadioDot, no click-outside (modal). */
  variant?: 'popover' | 'inline'
}

export function FilterDropdown({
  id,
  currentKey,
  options,
  onChange,
  fixedLabel,
  labelPrefix = '',
  variant = 'popover'
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLabel =
    fixedLabel ??
    labelPrefix + (options.find(o => o.key === currentKey)?.label ?? '')

  const toggle = () => setIsOpen(prev => !prev)
  const close = () => setIsOpen(false)

  const handleSelect = (option: DropdownOption) => {
    if (option.disabled) return
    onChange(option.key)
    close()
  }

  // Click-outside detection — only relevant for popover (inline lives inside a modal)
  useEffect(() => {
    if (variant !== 'popover') return

    const onDocumentClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        close()
      }
    }

    document.addEventListener('click', onDocumentClick)
    return () => document.removeEventListener('click', onDocumentClick)
  }, [variant])

  const arrowIcon = isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'

  if (variant === 'inline') {
    return (
      <S.InlineContainer id={id}>
        <S.InlineTrigger type="button" onClick={toggle}>
          {currentLabel}
          <span className="material-icons">{arrowIcon}</span>
        </S.InlineTrigger>

        {isOpen && (
          <S.OptionList>
            {options.map(option => (
              <S.OptionItem
                key={option.key}
                isDisabled={option.disabled}
                onClick={() => handleSelect(option)}
              >
                <S.RadioDot isSelected={option.key === currentKey} />
                {option.label}
              </S.OptionItem>
            ))}
          </S.OptionList>
        )}
      </S.InlineContainer>
    )
  }

  return (
    <S.PopoverContainer id={id} ref={containerRef}>
      <S.PopoverTrigger type="button" onClick={toggle}>
        {currentLabel}
        <span className="material-icons">{arrowIcon}</span>
      </S.PopoverTrigger>

      <S.PopoverPanel
        isOpen={isOpen}
        style={{ minWidth: containerRef.current?.offsetWidth ?? 'auto' }}
      >
        <S.OptionList>
          {options.map(option => (
            <S.OptionItem
              key={option.key}
              isDisabled={option.disabled}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </S.OptionItem>
          ))}
        </S.OptionList>
      </S.PopoverPanel>
    </S.PopoverContainer>
  )
}
